import { http } from "@/utils/http";
import { AxiosResponse } from "axios";
import {
  INewTask,
  ITask,
  ITaskAssign,
  ITaskComment,
  ITaskFilter,
  ITaskEvent,
  IMyTaskFilter,
  ITaskCloseRequest,
} from "@/features/tasks/task.types";
import { cleanParams } from "@/utils/cleanParams";

export const taskService = {
  createTask(payload: INewTask): Promise<AxiosResponse> {
    return http.post("/tasks", payload);
  },

  getTasks(filter?: ITaskFilter): Promise<AxiosResponse> {
    return http.get<ITask[]>("/tasks", {
      params: cleanParams(filter),
    });
  },

  getAlertQueueCount(team_id?: string): Promise<AxiosResponse> {
    if (team_id) {
      return http.get(`/tasks/alert-queue/count?team_id=${team_id}`);
    } else {
      return http.get("/tasks/alert-queue/count");
    }
  },

  takeAlert(): Promise<AxiosResponse> {
    return http.post("/tasks/take-alert");
  },

  getMyQueue(filter?: IMyTaskFilter): Promise<AxiosResponse> {
    return http.get("/tasks/my-queue", {
      params: cleanParams(filter),
    });
  },

  getTaskById(taskId: string): Promise<AxiosResponse> {
    return http.get<ITask>(`/tasks/${taskId}`);
  },

  getTaskEvents(taskId: string): Promise<AxiosResponse> {
    return http.get<ITaskEvent[]>(`/tasks/${taskId}/events`);
  },

  startTask(taskId: string): Promise<AxiosResponse> {
    return http.post(`/tasks/${taskId}/start`);
  },

  holdTask(taskId: string): Promise<AxiosResponse> {
    return http.post(`/tasks/${taskId}/hold`, { reason: "hold" });
  },

  resumeTask(taskId: string): Promise<AxiosResponse> {
    return http.post(`/tasks/${taskId}/resume`);
  },

  doneTask(taskId: string): Promise<AxiosResponse> {
    return http.post(`/tasks/${taskId}/done`);
  },

  closeTask(taskId: string): Promise<AxiosResponse> {
    return http.post(`/tasks/${taskId}/close`);
  },

  failTask(taskId: string): Promise<AxiosResponse> {
    return http.post(`/tasks/${taskId}/fail`);
  },

  assignTask(taskId: string, payload: ITaskAssign): Promise<AxiosResponse> {
    return http.post(`/tasks/${taskId}/assign`, payload);
  },

  autoAssign(
    shift_run_id: string,
    limit: number,
    team_id: string
  ): Promise<AxiosResponse> {
    return http.post("/tasks/auto-assign", { shift_run_id, limit, team_id });
  },

  addComment(taskId: string, payload: ITaskComment): Promise<AxiosResponse> {
    return http.post(`/tasks/${taskId}/comment`, payload);
  },

  getRecommendedWorker(taskId: string): Promise<AxiosResponse> {
    return http.get(`/tasks/${taskId}/recommended-worker`);
  },

  changeTaskWorkType(taskId: string, payload: any): Promise<AxiosResponse> {
    return http.patch(`/tasks/${taskId}/work-type`, payload);
  },

  addTaskWorkType(taskId: string, workTypeId: string): Promise<AxiosResponse> {
    return http.post(`/tasks/${taskId}/work-types`, {
      work_type_id: workTypeId,
    });
  },

  removeTaskWorkType(
    taskId: string,
    workTypeId: string
  ): Promise<AxiosResponse> {
    return http.delete(`/tasks/${taskId}/work-types/${workTypeId}`);
  },

  createInformNote(payload: any): Promise<AxiosResponse> {
    return http.post("/tasks/inform-note", payload);
  },

  getTaskTimeline(taskId: string): Promise<AxiosResponse> {
    return http.get(`/tasks/${taskId}/timeline`);
  },

  getReassignSuggestions(taskId: string): Promise<AxiosResponse> {
    return http.get(
      `/tasks/${taskId}/reassign-suggestions?min_minutes=1&limit=20`
    );
  },

  reassignTask(taskId: string, payload: any): Promise<AxiosResponse> {
    return http.post(`/tasks/${taskId}/reassign`, payload);
  },

  createCloseRequest(
    taskId: string,
    reason: string
  ): Promise<AxiosResponse<ITaskCloseRequest>> {
    return http.post(`/tasks/${taskId}/close-request`, {
      reason,
    });
  },

  getCloseRequests(
    taskId: string
  ): Promise<AxiosResponse<ITaskCloseRequest[]>> {
    return http.get(`/tasks/${taskId}/close-requests`);
  },

  getPendingCloseRequests(
    limit = 100
  ): Promise<AxiosResponse<ITaskCloseRequest[]>> {
    return http.get("/task-close-requests/pending", {
      params: { limit },
    });
  },

  approveCloseRequest(
    requestId: string,
    comment = ""
  ): Promise<AxiosResponse<ITaskCloseRequest>> {
    return http.post(`/task-close-requests/${requestId}/approve`, {
      comment: comment || null,
    });
  },

  rejectCloseRequest(
    requestId: string,
    comment: string
  ): Promise<AxiosResponse<ITaskCloseRequest>> {
    return http.post(`/task-close-requests/${requestId}/reject`, { comment });
  },
};
